import React from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createTribe } from "@/data/requests/tribe-requests";
import { Plus, Users } from "lucide-react";

export function CreateTribeDialog({ children }) {
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const createMutation = useMutation({
    mutationFn: createTribe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tribes"] });
      toast({
        title: "Tribe created",
        description: "Your tribe has been successfully created.",
      });
      setOpen(false);
      reset();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create tribe. Please try again.",
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data) => {
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <Plus className="mr-2 h-4 w-4" /> Create Tribe
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Tribe</DialogTitle>
          <DialogDescription>
            Start a new community. Invite friends to join your journey.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Tribe Name</Label>
            <Input 
              id="name" 
              placeholder="e.g. Morning Runners" 
              {...register("name", { required: true })} 
            />
            {errors.name && <span className="text-xs text-red-500">Name is required</span>}
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description" 
              placeholder="What is this tribe about?" 
              {...register("description")} 
            />
          </div>
        </form>
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={createMutation.isPending}>
            {createMutation.isPending ? "Creating..." : "Create Tribe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
